import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Textarea } from '@artstorefronts/ui';

interface ConnectNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  displayName: string;
  sending: boolean;
  /** Parent aborts pending connect when user cancels. */
  onCancel: () => void;
  /** Parent fires doSend(note) on submit. */
  onSend: (note: string | null) => void;
}

export function ConnectNoteDialog({
  open,
  onOpenChange,
  displayName,
  sending,
  onCancel,
  onSend,
}: ConnectNoteDialogProps) {
  const { t } = useTranslation('social');
  const [message, setMessage] = useState('');
  const actionTakenRef = useRef(false);

  // Parent may close without Radix onOpenChange (e.g. superseding another connect).
  useEffect(() => {
    if (!open) {
      setMessage('');
      actionTakenRef.current = false;
    }
  }, [open]);

  function handleOpenChange(next: boolean) {
    if (!next && open && !actionTakenRef.current) {
      onCancel();
    }
    onOpenChange(next);
  }

  function handleCancel() {
    actionTakenRef.current = true;
    onCancel();
    handleOpenChange(false);
  }

  function send(note: string | null) {
    if (sending || actionTakenRef.current) {
      return;
    }
    actionTakenRef.current = true;
    onSend(note);
    handleOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('connection.intro-title', { name: displayName })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-500">{t('connection.intro-description')}</p>
          <Textarea
            className="min-h-28"
            placeholder={t('connection.intro-placeholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={sending} onClick={handleCancel}>
            {t('connection.cancel')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={sending}
            onClick={() => {
              send(null);
            }}
          >
            {t('connection.connect-without-note')}
          </Button>
          <Button
            type="button"
            disabled={sending}
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => {
              send(message.trim() || null);
            }}
          >
            {sending ? t('connection.sending') : t('connection.send-intro')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
